const DEBUG_CALCRECOMRATE = 0;

/**
 * Simplified Recommendation Rate Calculator
 * 
 * Calculates admission likelihood (0-100%) based on user's score vs. program's admission quartiles.
 */

export interface AdmissionScores {
  LQ?: number;   // Lower Quartile (25th percentile)
  MED?: number;  // Median (50th percentile)
  UQ?: number;   // Upper Quartile (75th percentile)
}

export interface UserScores {
  min?: number;      // User's minimum expected score (can be 0 or undefined if N/A)
  current?: number;  // User's current/actual score (required)
  max?: number;      // User's maximum expected score (can be 0 or undefined if N/A)
}

/**
 * Calculate recommendation rate (0-100%) for program admission.
 * 
 * Simple algorithm:
 * - If current >= max possible: 100% (excellent - at or above maximum achievable)
 * - If MED <= current < max possible: 80-100% (very good - between median and max)
 * - If LQ <= current < MED: 50-80% (good - between lower quartile and median)
 * - If current < LQ: 0-50% (challenging - below lower quartile)
 * 
 * The upper bound uses userScores.max (maximum possible score) if available,
 * otherwise falls back to UQ or median * 1.05.
 * 
 * Handles missing/zero values gracefully:
 * - If min or max are 0/undefined, they're ignored
 * - If LQ or UQ are 0/undefined, calculation uses MED only
 * - Returns undefined only if current score or MED is missing
 * 
 * @param userScores - User's scores { min, current, max }
 * @param admissionScores - Program's quartiles { LQ, MED, UQ }
 * @param programId - Optional program ID for debugging
 * @param highestAttainableScore - Optional max possible programme score (all 5**), used as upper bound fallback
 * @returns Recommendation rate 0-100, or undefined if insufficient data
 */
export function calcRecomRate(
  userScores: UserScores,
  admissionScores: AdmissionScores,
  programId?: string,
  highestAttainableScore?: number
): number | undefined {
  // debugger;
  if (DEBUG_CALCRECOMRATE) {
    console.log('🚀 [calcRecomRate] ENTRY' + (programId ? ` for ${programId}` : ''), {
      userScores,
      admissionScores
    });
  }

  // 1. Extract user's current score (REQUIRED)
  const current = userScores?.current;
  if (!current || current <= 0) {
    return undefined;
  }

  // 2. Extract admission quartiles (at least MED or LQ required)
  const LQ = admissionScores?.LQ || 0;
  const MED = admissionScores?.MED || 0;
  const UQ = admissionScores?.UQ || 0;

  // Must have at least MED or LQ to calculate
  if (MED <= 0 && LQ <= 0) {
    if (DEBUG_CALCRECOMRATE) {
      console.log('❌ [calcRecomRate] No valid admission scores (need MED or LQ)');
    }
    // debugger;
    return undefined;
  }

  // 3. Set up calculation values with fallbacks
  const median = MED > 0 ? MED : LQ;
  const lowerQuartile = LQ > 0 ? LQ : median * 0.95;
  
  // Use user's max score as upper bound ONLY if it represents a real range (max > current)
  // When max === current, the user hasn't set grade ranges, so fall through to programme max
  const maxPossible = userScores?.max || 0;
  const progMax = highestAttainableScore || 0;
  const upperBound = (maxPossible > 0 && maxPossible > current) ? maxPossible : (progMax > 0 ? progMax : (UQ > 0 ? UQ * 1.2 : median * 1.2));

  if (DEBUG_CALCRECOMRATE) {
    console.log('📊 [calcRecomRate] Calculating with:', {
      current,
      lowerQuartile,
      median,
      upperBound,
      maxPossible
    });
  }
  // 4. Calculate recommendation rate
  let rate: number;

  if (current >= upperBound) {
    // At or above maximum possible score → 100% (excellent)
    rate = 100;
    if (DEBUG_CALCRECOMRATE) {
      console.log('✅ Current >= Max Possible → 100%');
    }

  } else if (current >= median) {
    // Between MED and max possible → 80-100% (very good, interpolated)
    const range = upperBound - median;
    const position = current - median;
    rate = 80 + (position / range) * 20;
    if (DEBUG_CALCRECOMRATE) {
      console.log('🟢 MED <= Current < Max → ' + rate.toFixed(1) + '%');
    }

  } else if (current >= lowerQuartile) {
    // Between LQ and MED → 50-80% (good, interpolated)
    const range = median - lowerQuartile;
    const position = current - lowerQuartile;
    rate = 50 + (position / range) * 30;
    if (DEBUG_CALCRECOMRATE) {
      console.log('🟡 LQ <= Current < MED → ' + rate.toFixed(1) + '%');
    }

  } else {
    // Below LQ → 0-50% (challenging, interpolated)
    // Assume 50% of LQ is the minimum viable score
    const minViableScore = lowerQuartile * 0.5;
    if (current >= minViableScore) {
      const range = lowerQuartile - minViableScore;
      const position = current - minViableScore;
      rate = (position / range) * 50;
    } else {
      rate = 0;
    }
    if (DEBUG_CALCRECOMRATE) {
      console.log('📉 Current < LQ → ' + rate.toFixed(1) + '%');
    }
  }

  // 5. Clamp to 0-100 and return
  rate = Math.max(0, Math.min(100, Math.round(rate)));
  if (DEBUG_CALCRECOMRATE) {
    console.log('🎯 [calcRecomRate] RESULT: ' + rate + '%');
  }

  return rate;
}

